import React, { useEffect } from "react";

import { useIndependenceDayTheme } from "../../lib/useIndependenceDayTheme";
/* w4t-flag and w4t-wheel live in this stylesheet and are reused rather than
 * redefined — see the deviations note at the top of wishes-strip.css. */
import "./independence-day-navbar.css";
import "./wishes-strip.css";

/* ------------------------------------------------------------------------
 * Independence Day wishes strip.
 *
 * A full-bleed band fixed to the bottom of the viewport, carrying one wish
 * that scrolls continuously right to left on a blended tricolour gradient,
 * with hand-drawn icons rather than emoji (emoji render as another font's
 * glyph — a different style and weight on every OS).
 *
 * Mounted ONCE in App.js, outside the Router, so it shows on every route
 * and no ancestor carries a transform/filter/contain that would re-anchor
 * the fixed positioning away from the viewport.
 *
 * Self-gated on the same hook as the rest of the theme; off the date it
 * renders null — no DOM, no body padding, nothing.
 *
 * While mounted it gives <body> 40px of bottom padding so the last content
 * on any page is not hidden behind the band. Doing it here rather than in
 * a stylesheet keeps the padding exactly co-terminous with the strip: it
 * appears with the band and is restored the moment the band unmounts.
 *
 * The ticker: the run holds the wish THREE times, and the run is rendered
 * TWICE with the track translating by -50% — exactly one run. Three repeats
 * keep the band populated at desktop width; the double render is what makes
 * the loop seamless. Both copies are identical; the second is aria-hidden
 * so a screen reader announces the wish once.
 * ---------------------------------------------------------------------- */

const WISH_A = "Happy Independence Day! Proud moments!";
const WISH_B = "Track smarter with WAY4TRACK";

/* ---- icons: drawn in the strip's own palette, no emoji ---- */

/* 3:2, three equal bands, navy Chakra, cloth waving. At 17px wide, 4 drawn
 * lines read as the spoked wheel; drawing 24 blurs into a solid disc. */
const Flag = () => (
    <span
        style={{
            flex: "none",
            width: "17px",
            height: "11.5px",
            borderRadius: "1px",
            overflow: "hidden",
            transformOrigin: "left center",
            animation: "w4t-flag 2.4s ease-in-out infinite",
        }}
    >
        <svg
            viewBox="0 0 900 600"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
            style={{ display: "block" }}
        >
            <rect width="900" height="200" fill="#FF9933" />
            <rect y="200" width="900" height="200" fill="#FFFFFF" />
            <rect y="400" width="900" height="200" fill="#138808" />
            <g transform="translate(450 300)">
                <circle r="74" fill="none" stroke="#000080" strokeWidth="12" />
                <circle r="18" fill="#000080" />
                <g stroke="#000080" strokeWidth="8">
                    <line x1="0" y1="-74" x2="0" y2="74" />
                    <line x1="0" y1="-74" x2="0" y2="74" transform="rotate(45)" />
                    <line x1="0" y1="-74" x2="0" y2="74" transform="rotate(90)" />
                    <line x1="0" y1="-74" x2="0" y2="74" transform="rotate(135)" />
                </g>
            </g>
        </svg>
    </span>
);

/* Marigold burst — a saffron bloom with a lighter centre and eight rays. */
const Burst = () => (
    <span style={{ flex: "none", width: "17px", height: "17px", display: "block" }}>
        <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" focusable="false" style={{ display: "block" }}>
            <g stroke="#E0821F" strokeWidth="1.5" strokeLinecap="round">
                <path d="M12 3.4 V6.4" />
                <path d="M12 17.6 V20.6" />
                <path d="M3.4 12 H6.4" />
                <path d="M17.6 12 H20.6" />
                <path d="M5.9 5.9 L8 8" />
                <path d="M16 16 L18.1 18.1" />
                <path d="M18.1 5.9 L16 8" />
                <path d="M8 16 L5.9 18.1" />
            </g>
            <circle cx="12" cy="12" r="4.4" fill="#FF9933" />
            <circle cx="12" cy="12" r="2" fill="#FFD79A" />
        </svg>
    </span>
);

/* Tracked hatchback, facing left — the direction the strip travels. Eight
 * tread notches, a three-spoke hub and an off-centre valve dot per tyre are
 * what make rotation legible at a 3.2-unit radius; each wheel sits in an
 * arch shadow so it reads as seated in the body rather than pasted on.
 * The gradient id w4tci is a shared constant on purpose: every <Car />
 * renders identical defs, so all instances resolve to the same paint. */
const Car = () => (
    <span
        style={{
            flex: "none",
            width: "30px",
            height: "17px",
            display: "block",
            animation: "w4t-icar 1.15s ease-in-out infinite",
        }}
    >
        <svg viewBox="0 0 30 17" width="30" height="17" aria-hidden="true" focusable="false" style={{ display: "block" }}>
            <defs>
                <linearGradient id="w4tci" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#5FBF52" />
                    <stop offset=".5" stopColor="#4FAE43" />
                    <stop offset="1" stopColor="#2F7228" />
                </linearGradient>
            </defs>
            <ellipse cx="8.6" cy="12.6" rx="4" ry="1.5" fill="#1F1F1F" opacity=".2" />
            <ellipse cx="21.6" cy="12.6" rx="4" ry="1.5" fill="#1F1F1F" opacity=".2" />
            <path
                d="M2 12.6 L2.3 9 Q2.6 7.3 5 6.9 L8.6 6.4 L11.8 3.2 Q13 2.2 14.9 2.2 L20.6 2.2 Q22.8 2.2 23.9 3.5 L26.4 6.6 Q28 7.9 28 9.9 L28 12.6 Z"
                fill="url(#w4tci)"
            />
            <path d="M12.4 6.2 L15 3.6 L18 3.6 L18 6.2 Z" fill="#DCEEFB" />
            <path d="M19.2 3.6 L22 3.6 L24.6 6.2 L19.2 6.2 Z" fill="#DCEEFB" />
            <rect x="18.4" y="3.6" width=".7" height="2.6" fill="#2F7228" />
            <rect x="2.1" y="8.4" width="2.4" height="1.6" rx=".7" fill="#FFE7A6" />
            <g style={{ transformOrigin: "8.6px 12.6px", animation: "w4t-wheel .42s linear infinite" }}>
                <circle cx="8.6" cy="12.6" r="3.2" fill="#23272C" />
                <path
                    d="M10.40 12.60 L11.45 12.60 M9.87 13.87 L10.62 14.62 M8.60 14.40 L8.60 15.45 M7.33 13.87 L6.58 14.62 M6.80 12.60 L5.75 12.60 M7.33 11.33 L6.58 10.58 M8.60 10.80 L8.60 9.75 M9.87 11.33 L10.62 10.58"
                    stroke="#525A62"
                    strokeWidth=".62"
                    strokeLinecap="round"
                />
                <circle cx="10.37" cy="11.21" r=".38" fill="#E8DFC8" />
                <circle cx="8.6" cy="12.6" r="1.3" fill="#C9D0D8" />
                <path
                    d="M6.52 12.60 L10.68 12.60 M7.56 10.80 L9.64 14.40 M9.64 10.80 L7.56 14.40"
                    stroke="#7E868F"
                    strokeWidth=".45"
                    strokeLinecap="round"
                />
            </g>
            <g style={{ transformOrigin: "21.6px 12.6px", animation: "w4t-wheel .42s linear infinite" }}>
                <circle cx="21.6" cy="12.6" r="3.2" fill="#23272C" />
                <path
                    d="M23.40 12.60 L24.45 12.60 M22.87 13.87 L23.62 14.62 M21.60 14.40 L21.60 15.45 M20.33 13.87 L19.58 14.62 M19.80 12.60 L18.75 12.60 M20.33 11.33 L19.58 10.58 M21.60 10.80 L21.60 9.75 M22.87 11.33 L23.62 10.58"
                    stroke="#525A62"
                    strokeWidth=".62"
                    strokeLinecap="round"
                />
                <circle cx="23.37" cy="11.21" r=".38" fill="#E8DFC8" />
                <circle cx="21.6" cy="12.6" r="1.3" fill="#C9D0D8" />
                <path
                    d="M19.52 12.60 L23.68 12.60 M20.56 10.80 L22.64 14.40 M22.64 10.80 L20.56 14.40"
                    stroke="#7E868F"
                    strokeWidth=".45"
                    strokeLinecap="round"
                />
            </g>
            <circle
                cx="15.4"
                cy="1.5"
                r="1"
                fill="#1A3A8F"
                style={{ animation: "w4t-wishes-ping 2.2s ease-in-out infinite" }}
            />
        </svg>
    </span>
);

/* ---- the ticker ---- */

const Item = () => (
    <span className="w4t-wishes__item">
        <Flag />
        <span className="w4t-wishes__text">{WISH_A}</span>
        <Burst />
        <Car />
        <span className="w4t-wishes__text">{WISH_B}</span>
    </span>
);

const Run = ({ hidden }) => (
    <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden={hidden || undefined}>
        <Item />
        <Item />
        <Item />
    </span>
);

export default function WishesStrip() {
    const festive = useIndependenceDayTheme();

    /* Keep the last content on every page clear of the band. Co-terminous
     * with the strip by construction: applied when it mounts on the date,
     * restored the moment it unmounts. */
    useEffect(() => {
        if (!festive) return undefined;

        const previous = document.body.style.paddingBottom;
        document.body.style.paddingBottom = "40px";
        return () => {
            /* removeProperty, not an empty-string assignment: jsdom ignores
             * the latter, and explicit removal is unambiguous everywhere */
            if (previous) document.body.style.paddingBottom = previous;
            else document.body.style.removeProperty("padding-bottom");
        };
    }, [festive]);

    if (!festive) return null;

    return (
        <div className="w4t-wishes">
            <div className="w4t-wishes__band">
                <div className="w4t-wishes__track">
                    <Run />
                    <Run hidden />
                </div>
                <div className="w4t-wishes__depth" />
                <div className="w4t-wishes__shine" />
                <div className="w4t-wishes__fade w4t-wishes__fade--left" />
                <div className="w4t-wishes__fade w4t-wishes__fade--right" />
                <div className="w4t-wishes__rim" />
            </div>
        </div>
    );
}
